<template>
  <div class="transactions-filters">
    <div class="transactions-filters__fields">
      <custom-date-picker
        class="transactions-filters__date-from"
        v-model:value="startDateInner"
        :label="$t('analytics.filter.date_from')"
      ></custom-date-picker>
      <custom-date-picker
        class="transactions-filters__date-to"
        v-model:value="endDateInner"
        :label="$t('analytics.filter.date_to')"
      ></custom-date-picker>
      <div class="transactions-filters__group">
        <custom-select
          v-model:value="filterType"
          class="transactions-filters__search-by"
          :options="availableFilterTypes"
          :title="$t('analytics.filter.search-by')"
        ></custom-select>
        <component
          v-bind="{ ...currentFilterComponentProps }"
          :is="currentFilterComponent"
          class="transactions-filters__search-value"
          v-model:value="currentFilterValue"
          :key="filterTypeChecked"
          v-if="filterType"
          @enter="applyFilters"
        ></component>
      </div>
    </div>
    <div class="transactions-filters__actions">
      <no-bg-button
        class="transactions-filters__actions-item"
        :label="$t('analytics.filter.actions.clear')"
        @click="resetFilters"
      ></no-bg-button>
      <primary-button
        class="transactions-filters__actions-item"
        :disabled="isTransactionsLoading"
        :label="$t('analytics.filter.actions.apply')"
        @click="applyFilters"
      ></primary-button>
    </div>
  </div>
</template>

<script>
import {$emit,} from '../../../../../../utils/gogocodeTransfer'
import NoBgButton from '@vue-app/components/base/button/NoBgButton.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomDatePicker from '@vue-app/components/base/input/CustomDatePicker.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import BaseTextInput from '@vue-app/components/base/input/BaseTextInput.vue'
import AmountRangeInput from '@vue-app/components/base/input/AmountRangeInput.vue'
import {mapState} from 'vuex'
import moment from 'moment'
import {cloneDeep} from 'lodash'

export default {
  name: 'TransactionsFilter',
  components: {
    CustomSelect,
    CustomDatePicker,
    PrimaryButton,
    NoBgButton,
    BaseTextInput,
    AmountRangeInput,
  },
  props: {
    autoInitFilters: {
      type: Boolean,
      default: false,
    },
    isTransactionsLoading: {
      type: Boolean,
      default: false,
    },
    filterObject: {
      type: Object,
      default() {
        return {
          dates: {
            from: null,
            to: null,
          },
          currentFilterType: null,
          activeFilters: {},
        }
      },
    },
    availableFilterTypes: {
      type: Array,
      default() {
        return []
      },
    },
    additionalFilters: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      startDateInner: null,
      endDateInner: null,
      filterType: null,
      filters: {},
      currentFilterValue: null, // локальное значение редактируемого фильтра
    }
  },
  computed: {
    ...mapState(['xpayOperationId', 'xpayOperationType']),

    filterTypeChecked() {
      return this.filterType?.id ?? this.filterType
    },

    currentFilterComponent() {
      if (!this.filterType) return null
      return this.additionalFilters[this.filterTypeChecked]?.component
    },

    currentFilterComponentProps() {
      const currentFilter = this.additionalFilters[this.filterTypeChecked]
      if (!currentFilter) return {}

      switch (currentFilter.component) {
        case 'CustomSelect':
          return {
            options: currentFilter.options,
            title: this.$t(currentFilter.title),
            valueKey: currentFilter.valueKey ?? null,
          }
        case 'BaseTextInput':
        case 'AmountRangeInput':
          return {
            title: this.$t(currentFilter.title),
          }
        default:
          return {}
      }
    },
  },
  watch: {
    filterObject: {
      handler: 'initFilters',
      immediate: true,
      deep: true,
    },

    filterType(newVal, oldVal) {
      if (oldVal) {
        const oldKey = oldVal.id ?? oldVal
        if (this.filters[oldKey]) {
          this.filters[oldKey].value = null
        }
      }

      const newKey = newVal?.id ?? newVal
      this.currentFilterValue = cloneDeep(this.filters[newKey]?.value) ?? null
    },
  },
  methods: {
    initFilters() {
      if (!this.filterObject?.dates) return

      this.filters = cloneDeep(this.additionalFilters)

      this.startDateInner = this.filterObject.dates.from
      this.endDateInner = this.filterObject.dates.to

      this.filterType = this.filterObject.currentFilterType

      if (this.filterObject?.activeFilters) {
        for (const filterKey in this.filterObject.activeFilters) {
          const value = this.filterObject.activeFilters[filterKey]
          if (this.filters[filterKey]) {
            this.filters[filterKey].value = value
          }
        }
      }

      // инициализация currentFilterValue
      if (this.filterType) {
        const key = this.filterTypeChecked
        this.currentFilterValue = cloneDeep(this.filters[key]?.value) ?? null
      }
    },

    applyFilters() {
      const _activeFilters = {}

      if (this.filterType) {
        const key = this.filterTypeChecked
        this.filters[key].value = cloneDeep(this.currentFilterValue)

        const filter = this.filters[key]
        if (filter.valueKey && key !== 'mid') {
          const raw = filter.value
          _activeFilters[key] = typeof raw === 'object'
              ? { ...raw, filterValue: raw[filter.valueKey] }
              : { filterValue: raw }
        } else {
          _activeFilters[key] = filter.value
        }
      }

      if (this.filters.operationId.value) {
        _activeFilters.operationId = this.filters.operationId.value
      }

      $emit(this, 'filterUpdated', {
        dates: {
          from: this.startDateInner,
          to: this.endDateInner,
        },
        currentFilterType: this.filterType,
        activeFilters: _activeFilters,
      }, 'apply')
    },


    resetFilters() {
      this.startDateInner = moment().subtract(1, 'hours').toDate()
      this.endDateInner = moment().toDate()
      this.filterType = null
      this.currentFilterValue = null

      $emit(this, 'filterReset', {
        dates: {
          from: this.startDateInner,
          to: this.endDateInner,
        },
        currentFilterType: null,
        activeFilters: {},
      }, 'reset')
    },
  },
  emits: ['filterUpdated', 'filterReset'],
}
</script>

<style lang="scss" scoped>
.transactions-filters {
  width: 100%;
  padding: 20px 24px 24px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  .transactions-filters__search-value {
    width: max-content;
  }

  &__fields {
    display: flex;
    align-items: stretch;
    margin-right: 60px;
  }

  &__actions {
    display: flex;
    align-items: center;
    margin-left: auto;

    &-item {
      margin-right: 15px;
    }
  }

  &__date-from {
    margin-right: 10px;
  }

  &__search-by {
    width: 191px;
  }

  &__group {
    display: flex;
    align-items: center;
    margin-left: 10px;
  }
}
@media screen and (max-width: 992px) {
  .transactions-filters {
    flex-direction: column;
    padding: 20px;
    &__fields {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      margin-right: 0;
    }

    &__group {
      margin-left: 0;
      margin-top: 10px;
      width: 100%;
      justify-content: space-between;
    }

    &__search-by,
    &__search-value {
      width: calc(50% - 5px);
    }

    &__actions {
      margin-top: 30px;
      width: 100%;
      display: flex;
      align-items: center;
      flex-direction: row-reverse;
      justify-content: space-between;

      &-item {
        width: calc(50% - 7px);
      }
    }

    &__date-from {
      width: calc(50% - 5px);
    }

    &__date-to {
      width: calc(50% - 5px);
    }
  }
}
</style>

<style lang="scss">
.transactions-filters {
  &__search-value {
    width: 200px;
    margin-left: 10px;

    .custom-select__list {
      max-height: 200px;
      overflow: auto;
    }
  }
}
</style>
